import { NON_SWIFT_COUNTRIES_ISO_CODES } from '@constants/enums';
import {
  CountryCodes,
  EFEX_NAME,
  SWIFT_NAME,
} from '@efex-components/common/Form/config';
import clientSvc from '@efex-services/client/clientSvc';
import { selectProfileData } from '@pages/profile/store/selectors';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CountriesToShowDefaultCurrency } from '@pages/clients/AddContactForm/constants';
import { Country } from '../../../configs/language';
import useImmediateApiCall from '../../../hooks/useImmediateApiCall';
import {
  CountryOption,
  CountryValue,
  UseCountryAutocompleteProps,
  UseCountryAutocompleteReturn,
} from './useCountryAutocomplete.types';

const useCountryAutocomplete = ({
  initialCountry,
  onCountryChange,
}: UseCountryAutocompleteProps = {}): UseCountryAutocompleteReturn => {
  const { t } = useTranslation();
  const { isAvailableChinaPayment } = useSelector(selectProfileData) || {};

  const { data: swiftDropdownOptions, retry: retrySwiftCountries } =
    useImmediateApiCall(() => clientSvc.getSwiftCountries('all'));

  const [currentCountry, setCurrentCountry] = useState<
    CountryValue | undefined
  >(initialCountry);

  const [selectedCountryOption, setSelectedCountryOption] =
    useState<CountryOption | null>(null);

  const autocompleteOptions: CountryOption[] = useMemo(() => {
    const dropdownOptionsCountry: { key: string; value: Country }[] = [
      { key: t('usa'), value: CountryCodes.USA },
      { key: t('mexico'), value: CountryCodes.MX },
      { key: t('col'), value: CountryCodes.COL },
    ];

    const options: CountryOption[] = [];

    options.push({
      id: 'efex-header',
      label: EFEX_NAME,
      value: 'efex-header',
      group: EFEX_NAME,
      isHeader: true,
    });

    const efexCountries: CountryOption[] = dropdownOptionsCountry.map(
      (country) => ({
        id: country.value,
        label: country.key,
        value: country.value,
        group: EFEX_NAME,
      })
    );
    options.push(...efexCountries);

    if (swiftDropdownOptions && swiftDropdownOptions.length > 0) {
      options.push({
        id: 'swift-header',
        label: t('ccSwift'),
        value: 'swift-header',
        group: SWIFT_NAME,
        isHeader: true,
      });

      const swiftCountries: CountryOption[] = swiftDropdownOptions
        ?.filter(
          ({ isoCode }: { isoCode: string }) =>
            !NON_SWIFT_COUNTRIES_ISO_CODES.includes(isoCode)
        )
        ?.sort((a, b) =>
          t(a.isoCode).localeCompare(t(b.isoCode), ['en', 'es'], {
            sensitivity: 'base',
          })
        )
        .map((country) => ({
          id: country.id,
          label: t(country.isoCode),
          value: country.id,
          group: SWIFT_NAME,
          isoCode: country.isoCode,
        }));
      options.push(...swiftCountries);
    }

    if (!isAvailableChinaPayment) {
      return options.filter(
        (option) => option.isoCode !== CountriesToShowDefaultCurrency.CN
      );
    }

    return options;
  }, [swiftDropdownOptions, t, isAvailableChinaPayment]);

  const handleAutocompleteChange = useCallback(
    (event: SyntheticEvent, value: CountryOption | null) => {
      if (value?.isHeader) {
        return;
      }

      setSelectedCountryOption(value);

      if (value) {
        const countryValue = value.value as CountryValue;
        setCurrentCountry(countryValue);
        onCountryChange?.(countryValue);
      } else {
        setCurrentCountry(undefined);
        onCountryChange?.(undefined);
      }
    },
    [onCountryChange]
  );

  const getTooltipMessage = useCallback(
    (group: typeof EFEX_NAME | typeof SWIFT_NAME): string =>
      group === EFEX_NAME
        ? t('ccEfexCountryMessage')
        : t('ccSwiftCountryMessage'),
    [t]
  );

  const isUSAOrSwiftCountry = useMemo(
    () =>
      currentCountry !== undefined &&
      (currentCountry === CountryCodes.USA ||
        typeof currentCountry === 'number'),
    [currentCountry]
  );

  useEffect(() => {
    if (currentCountry && autocompleteOptions.length > 0) {
      const initialOption = autocompleteOptions.find(
        (option) => option.value === currentCountry && !option.isHeader
      );

      if (initialOption && initialOption !== selectedCountryOption) {
        setSelectedCountryOption(initialOption);
      }
    }
  }, [currentCountry, autocompleteOptions, selectedCountryOption]);

  return {
    currentCountry,
    selectedCountryOption,
    autocompleteOptions,
    isLoading: Boolean(!swiftDropdownOptions),
    error: null,
    handleAutocompleteChange,
    setCurrentCountry,
    getTooltipMessage,
    isUSAOrSwiftCountry,
    retrySwiftCountries,
  };
};

export default useCountryAutocomplete;
export type { CountryOption, CountryValue, UseCountryAutocompleteReturn };
